import { useDragLayer } from 'react-dnd'
import { DragItemTypes, tabReorderActiveRef } from './dnd-types'

/**
 * Custom drag layer for tab library drops.
 *
 * With TouchBackend (mouse events), there is no native browser drag ghost.
 * This component renders a floating label that follows the cursor during
 * react-dnd drags (tab → library drops). It only renders for EDITOR_TAB
 * drag type to avoid interfering with other drag operations.
 */
export function TabDragLayer() {
  /* eslint-disable @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access */
  const { isDragging, item, itemType, currentOffset } = useDragLayer((monitor) => ({
    isDragging: monitor.isDragging(),
    item: monitor.getItem(),
    itemType: monitor.getItemType(),
    currentOffset: monitor.getSourceClientOffset(),
  }))

  // Only render for editor tab drags (tab → library drops).
  // Query reordering within the library has its own visual feedback.
  // Suppress during tab reorder (horizontal drag) to avoid showing the label.
  if (!isDragging || !currentOffset) return null
  if (itemType !== DragItemTypes.EDITOR_TAB) return null
  if (tabReorderActiveRef.current) return null

  const label = (item?.label ?? item?.name ?? 'Query') as string
  /* eslint-enable @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access */

  return (
    <div
      style={{
        position: 'fixed',
        pointerEvents: 'none',
        left: 0,
        top: 0,
        transform: `translate(${currentOffset.x}px, ${currentOffset.y}px)`,
        zIndex: 9999,
      }}
    >
      <div
        className="bg-gray-800 dark:bg-gray-700 border border-gray-600 dark:border-gray-500 text-white text-xs font-medium rounded px-3 py-1.5 shadow-lg whitespace-nowrap"
        style={{ opacity: 0.9 }}
      >
        {label}
      </div>
    </div>
  )
}
